import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState(''); // State for success message
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Get location for state

  useEffect(() => {
    document.title = 'Logga in - TrafikBevakning';
    document.body.style.backgroundColor = '#f5f5f7';

    // Check for passed state (email and message)
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear state to prevent message from showing on refresh
      window.history.replaceState({}, document.title);
    }

    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !email.includes('@')) {
      setError('Giltig e-postadress krävs');
      return;
    }

    if (!password) {
      setError('Lösenord krävs');
      return;
    }

    setIsLoading(true);

    const result = await login(email, password, rememberMe);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-title">Logga in</h1>
          <p className="auth-subtitle">Välkommen tillbaka till TrafikBevakning</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {successMessage && (
            <div
              className="success-message"
              style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '10px',
                borderRadius: '4px',
                marginBottom: '15px',
                textAlign: 'center',
              }}
            >
              {successMessage}
            </div>
          )}
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">E-postadress</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="din.email@example.com"
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="password">Lösenord</label>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  color: '#666',
                  cursor: 'pointer',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                title={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ditt lösenord"
              disabled={isLoading}
              autoComplete="current-password"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '8px',
              }}
            >
              <Link
                to="/forgot-password"
                style={{ fontSize: '13px', color: '#666', textDecoration: 'none' }}
              >
                Glömt lösenordet?
              </Link>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0 12px' }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{
                width: '15px',
                height: '15px',
                accentColor: '#ff1616',
                cursor: 'pointer',
                flexShrink: 0,
                verticalAlign: 'middle',
                margin: 0,
              }}
            />
            <label
              htmlFor="rememberMe"
              style={{
                fontSize: '13px',
                color: '#666',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: '500',
                lineHeight: '15px',
                margin: 0,
              }}
            >
              Kom ihåg mig
            </label>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Loggar in...
              </>
            ) : (
              'Logga in'
            )}
          </button>

          <div className="auth-links">
            <p>
              Har du inget konto? <Link to="/register">Skapa konto</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
